import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, Filter, Printer, RotateCcw, Gauge, Calendar } from 'lucide-react';

const FuelReportModal = ({ apiCall, vehicles: initialVehicles, onClose, showError, showSuccess }) => {
  const [fuelLogs, setFuelLogs] = useState([]);
  const [vehicles, setVehicles] = useState(initialVehicles || []);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    vehicleId: '',
    fuelType: '',
    category: '',
    driverName: ''
  });
  const [showFilters, setShowFilters] = useState(true);
  const [reportType, setReportType] = useState('detailed');
  const printRef = useRef(null);
  const isPrintingRef = useRef(false);

  // Get current user info from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = currentUser.fullName || currentUser.username || 'Unknown User';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('dateFrom', filters.dateFrom);
      params.append('dateTo', filters.dateTo);
      if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
      if (filters.fuelType) params.append('fuelType', filters.fuelType);
      if (filters.category) params.append('category', filters.category);
      if (filters.driverName) params.append('driverName', filters.driverName);

      const logsData = await apiCall(`/gas/logs?${params.toString()}`);
      
      if (vehicles.length === 0) {
        const vehiclesData = await apiCall('/gas/vehicles');
        setVehicles(vehiclesData || []);
      }
      
      setFuelLogs(logsData || []);
      
    } catch (error) {
      console.error('Error loading fuel report:', error);
      if (showError) showError('Error loading fuel report: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, filters, vehicles, showError]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const clearFilters = () => {
    setFilters({
      dateFrom: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      vehicleId: '',
      fuelType: '',
      category: '',
      driverName: ''
    });
  };

  const exportCSV = () => {
    if (fuelLogs.length === 0) {
      if (showError) showError('No data to export');
      return;
    }
    const headers = ['Date', 'Vehicle', 'Vehicle Type', 'Category', 'Fuel Type', 'Driver', 'Odometer (km)', 'Volume (L)', 'Price/L (USD)', 'Total Cost (USD)', 'Notes'];

    const data = fuelLogs.map(log => [
      new Date(log.FuelDate).toLocaleDateString(),
      log.VehicleNumber,
      log.VehicleType,
      log.Category || 'N/A',
      log.FuelType,
      log.DriverName || 'N/A',
      log.OdometerReading.toFixed(1),
      log.Volume.toFixed(2),
      log.Cost && log.Volume > 0 ? (log.Cost / log.Volume).toFixed(3) : 'Pending',
      log.Cost ? log.Cost.toFixed(2) : 'Pending',
      log.Notes || ''
    ]);

    const csv = [headers, ...data].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fuel_report_${filters.dateFrom}_to_${filters.dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    if (showSuccess) showSuccess('Fuel report exported successfully');
  };

  const handlePrint = () => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = true;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      if (showError) showError('Please allow popups to print the report');
      isPrintingRef.current = false;
      return;
    }

    const printContent = printRef.current;

const htmlContent = `
  <!DOCTYPE html>
  <html>
    <head>
      <title>Fuel Report</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 8px; background: white; font-size: 13px; }
        
        .report-header { border-bottom: 3px solid #1f2937; padding-bottom: 6px; margin-bottom: 8px; }
        .report-header h1 { font-size: 26px; color: #1f2937; margin-bottom: 6px; font-weight: bold; }
        .report-header h2 { font-size: 20px; color: #4b5563; margin-bottom: 6px; font-weight: 600; }
        .report-header .info { display: flex; justify-content: space-between; font-size: 13px; color: #4b5563; line-height: 1.5; }
        .report-header .info strong { color: #1f2937; }
        .report-header .info p { margin-bottom: 0; }
        
        .section { border: 2px solid #d1d5db; border-radius: 6px; padding: 6px; margin-bottom: 6px; page-break-inside: avoid; }
        .section h3 { font-size: 16px; color: #1f2937; margin-bottom: 6px; font-weight: bold; }
        
        .side-by-side { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; page-break-inside: avoid; }
        .side-by-side .section { margin-bottom: 0; }
        
        .breakdown-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
        .breakdown-item { border: 2px solid #e5e7eb; border-radius: 4px; padding: 6px; background: #f9fafb; page-break-inside: avoid; }
        .breakdown-item h4 { font-size: 14px; color: #1f2937; margin-bottom: 4px; font-weight: bold; }
        .breakdown-item .subtitle { font-size: 12px; color: #6b7280; margin-bottom: 6px; line-height: 1.4; }
        .breakdown-item .detail { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 3px; line-height: 1.4; }
        .breakdown-item .detail .label { color: #6b7280; }
        .breakdown-item .detail .value { font-weight: 600; }
        
        table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 6px; table-layout: fixed; }
        thead tr { background: #f3f4f6; border: 2px solid #d1d5db; }
        th { padding: 9px 7px; text-align: left; font-weight: bold; color: #374151; border: 1px solid #d1d5db; line-height: 1.4; }
        th:nth-child(1) { width: 8%; } /* Date */
        th:nth-child(2) { width: 9%; } /* Vehicle */
        th:nth-child(3) { width: 11%; } /* Type */
        th:nth-child(4) { width: 9%; } /* Category */
        th:nth-child(5) { width: 10%; } /* Fuel Type */
        th:nth-child(6) { width: 14%; } /* Driver */
        th:nth-child(7) { width: 10%; } /* Odometer */
        th:nth-child(8) { width: 10%; } /* Volume */
        th:nth-child(9) { width: 9%; } /* Price/L */
        th:nth-child(10) { width: 10%; } /* Cost */
        tbody tr { border: 1px solid #d1d5db; }
        td { padding: 8px 6px; border: 1px solid #d1d5db; line-height: 1.4; word-wrap: break-word; }
        tfoot tr { background: #e5e7eb; border: 2px solid #d1d5db; font-weight: bold; }
        tfoot td { padding: 9px 7px; }
        .pending { font-style: italic; color: #ea580c; }
        
        .signature-section { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-top: 15px; padding-top: 12px; border-top: 2px solid #e5e7eb; page-break-inside: avoid; }
        .signature-box { text-align: center; }
        .signature-line { border-top: 2px solid #1f2937; margin-top: 35px; padding-top: 6px; font-weight: bold; color: #1f2937; font-size: 14px; }
        .signature-label { font-size: 12px; color: #6b7280; margin-top: 4px; }
        
        .report-footer { margin-top: 12px; padding-top: 10px; border-top: 2px solid #d1d5db; font-size: 11px; color: #6b7280; text-align: center; line-height: 1.5; }
        
        @media print {
          body { padding: 3px; }
          .report-header { padding-bottom: 5px; margin-bottom: 6px; }
          .report-header h1 { margin-bottom: 3px; }
          .report-header h2 { margin-bottom: 3px; }
          .section { margin-bottom: 5px; padding: 5px; }
          .section h3 { margin-bottom: 4px; }
          .breakdown-item { padding: 4px; }
          .breakdown-item h4 { margin-bottom: 2px; }
          .breakdown-item .subtitle { margin-bottom: 2px; }
          .side-by-side { margin-bottom: 5px; gap: 5px; }
          .breakdown-grid { gap: 4px; }
          table { font-size: 9px; margin-top: 3px; }
          th, td { padding: 5px 3px; }
          .signature-section { gap: 8px; margin-top: 8px; padding-top: 6px; }
          .signature-line { margin-top: 20px; }
          .report-footer { margin-top: 6px; padding-top: 5px; }
          @page { margin: 0.25cm; size: A4; }
        }
      </style>
    </head>
    <body>${printContent.innerHTML}</body>
  </html>
`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
          isPrintingRef.current = false;
        }, 100);
      }, 250);
    };
  };

  const handleDailyView = () => {
    const today = new Date().toISOString().split('T')[0];
    setReportType('daily');
    setFilters({
      ...filters,
      dateFrom: today,
      dateTo: today
    });
  };

  // Get filter display text
  const getCategoryFilterText = () => {
    if (!filters.category) return 'All Categories';
    return filters.category;
  };

  const getFuelTypeFilterText = () => {
    if (!filters.fuelType) return 'All Fuel Types';
    return filters.fuelType;
  };

  // Analytics calculations
  const totalCost = fuelLogs.reduce((sum, log) => sum + (log.Cost || 0), 0);
  const totalVolume = fuelLogs.reduce((sum, log) => sum + (log.Volume || 0), 0);
  const pendingCount = fuelLogs.filter(log => !log.Cost).length;

  // Group by vehicle with mileage tracking
  const vehicleStats = {};
  fuelLogs.forEach(log => {
    if (!vehicleStats[log.VehicleNumber]) {
      const vehicleInfo = vehicles.find(v => v.VehicleNumber === log.VehicleNumber);
      vehicleStats[log.VehicleNumber] = {
        vehicleType: log.VehicleType,
        category: log.Category,
        fuelType: vehicleInfo?.FuelType || 'N/A',
        primaryDriver: vehicleInfo?.PrimaryDriverName || 'N/A',
        drivers: new Set(),
        totalCost: 0,
        totalVolume: 0,
        trips: 0,
        odometerReadings: []
      };
    }
    vehicleStats[log.VehicleNumber].totalCost += log.Cost || 0;
    vehicleStats[log.VehicleNumber].totalVolume += log.Volume || 0;
    vehicleStats[log.VehicleNumber].trips += 1;
    
    if (log.DriverName && log.DriverName !== 'N/A' && log.DriverName !== '-' && log.DriverName.trim() !== '') {
      vehicleStats[log.VehicleNumber].drivers.add(log.DriverName);
    }
    
    vehicleStats[log.VehicleNumber].odometerReadings.push({
      date: log.FuelDate,
      reading: log.OdometerReading
    });
  });

  Object.keys(vehicleStats).forEach(vehicleNumber => {
    const stats = vehicleStats[vehicleNumber];
    stats.odometerReadings.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    if (stats.odometerReadings.length >= 2) {
      const firstReading = stats.odometerReadings[0].reading;
      const lastReading = stats.odometerReadings[stats.odometerReadings.length - 1].reading;
      stats.totalDistance = lastReading - firstReading;
      stats.avgFuelEfficiency = stats.totalDistance > 0 ? stats.totalDistance / stats.totalVolume : 0;
      stats.firstOdometer = firstReading;
      stats.lastOdometer = lastReading;
    } else if (stats.odometerReadings.length === 1) {
      stats.firstOdometer = stats.odometerReadings[0].reading;
      stats.lastOdometer = stats.odometerReadings[0].reading;
      stats.totalDistance = 0;
      stats.avgFuelEfficiency = 0;
    }
    
    stats.driversArray = Array.from(stats.drivers);
  });

  const categoryStats = {};
  fuelLogs.forEach(log => {
    const cat = log.Category || 'Uncategorized';
    if (!categoryStats[cat]) {
      categoryStats[cat] = {
        totalCost: 0,
        totalVolume: 0,
        count: 0,
        vehicles: new Set()
      };
    }
    categoryStats[cat].totalCost += log.Cost || 0;
    categoryStats[cat].totalVolume += log.Volume || 0;
    categoryStats[cat].count += 1;
    categoryStats[cat].vehicles.add(log.VehicleNumber);
  });

  const fuelTypeStats = {};
  fuelLogs.forEach(log => {
    if (!fuelTypeStats[log.FuelType]) {
      fuelTypeStats[log.FuelType] = {
        totalCost: 0,
        totalVolume: 0,
        count: 0
      };
    }
    fuelTypeStats[log.FuelType].totalCost += log.Cost || 0;
    fuelTypeStats[log.FuelType].totalVolume += log.Volume || 0;
    fuelTypeStats[log.FuelType].count += 1;
  });

  const gasolineLogs = fuelLogs.filter(log => 
    log.FuelType === 'Gasoline 95' || log.FuelType === 'Gasoline 98'
  );
  const mazoutLogs = fuelLogs.filter(log => 
    log.FuelType === 'Mazout' || log.FuelType === 'Diesel'
  );

  const gasolineTotalVolume = gasolineLogs.reduce((sum, log) => sum + (log.Volume || 0), 0);
  const gasolineTotalCost = gasolineLogs.reduce((sum, log) => sum + (log.Cost || 0), 0);
  const gasolineAvgCostPerLiter = gasolineTotalVolume > 0 && gasolineTotalCost > 0 ? gasolineTotalCost / gasolineTotalVolume : 0;

  const mazoutTotalVolume = mazoutLogs.reduce((sum, log) => sum + (log.Volume || 0), 0);
  const mazoutTotalCost = mazoutLogs.reduce((sum, log) => sum + (log.Cost || 0), 0);
  const mazoutAvgCostPerLiter = mazoutTotalVolume > 0 && mazoutTotalCost > 0 ? mazoutTotalCost / mazoutTotalVolume : 0;

  const uniqueCategories = [...new Set(vehicles.map(v => v.Category).filter(Boolean))];
  const hasActiveFilters = filters.vehicleId || filters.fuelType || filters.category || filters.driverName;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl z-10">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
                  <Printer size={28} />
                  Fuel Report
                </h2>
                <p className="text-blue-100">Comprehensive fuel consumption and mileage analysis</p>
              </div>
              <div className="flex gap-3 items-center">
                <div className="bg-white rounded-lg p-1 flex gap-1">
                  <button
                    onClick={() => setReportType('detailed')}
                    className={'px-3 py-1 rounded text-sm font-semibold transition-colors ' + (reportType === 'detailed' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100')}
                  >
                    Detailed
                  </button>
                  <button
                    onClick={handleDailyView}
                    className={'px-3 py-1 rounded text-sm font-semibold transition-colors flex items-center gap-1 ' + (reportType === 'daily' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100')}
                  >
                    <Calendar size={14} />
                    Daily
                  </button>
                </div>
                <button
                  onClick={handlePrint}
                  className="bg-white text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all"
                >
                  <Printer size={18} />
                  Print
                </button>
                <button
                  onClick={exportCSV}
                  disabled={fuelLogs.length === 0}
                  className="bg-white text-green-600 hover:bg-green-50 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Download size={18} />
                  Export CSV
                </button>
                <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-6 pb-0">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-6">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    <Filter size={18} />
                    Filters
                    {loading && (
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></span>
                    )}
                  </h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-lg font-semibold flex items-center gap-1 transition-colors"
                    >
                      <RotateCcw size={12} />
                      Reset Filters
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-semibold"
                >
                  {showFilters ? 'Hide' : 'Show'}
                </button>
              </div>

              {showFilters && (
                <div className="grid md:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Date From</label>
                    <input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Date To</label>
                    <input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Vehicle</label>
                    <select
                      value={filters.vehicleId}
                      onChange={(e) => setFilters({...filters, vehicleId: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Vehicles</option>
                      {vehicles.map(v => (
                        <option key={v.VehicleID} value={v.VehicleID}>
                          {v.VehicleNumber} - {v.VehicleType}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Category</label>
                    <select
                      value={filters.category}
                      onChange={(e) => setFilters({...filters, category: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Categories</option>
                      {uniqueCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Fuel Type</label>
                    <select
                      value={filters.fuelType}
                      onChange={(e) => setFilters({...filters, fuelType: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Types</option>
                      <option value="Gasoline 95">Gasoline 95</option>
                      <option value="Gasoline 98">Gasoline 98</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Mazout">Mazout</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 pt-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Loading report...</p>
                </div>
              </div>
            ) : (
              <div ref={printRef}>
  {/* Print Header */}
  <div className="report-header">
    <div style={{textAlign: 'center', marginBottom: '10px'}}>
      <h1 style={{fontSize: '28px', fontWeight: 'bold', marginBottom: '4px', color: '#1f2937'}}>
        Collège des Apôtres - Jounieh
      </h1>
      <h2 style={{fontSize: '18px', color: '#4b5563', fontWeight: '600'}}>
        Petrol Station
      </h2>
    </div>
    <h1 style={{borderTop: '2px solid #d1d5db', paddingTop: '10px'}}>
      Fuel Consumption Report{reportType === 'daily' ? ' - Daily Summary' : ''}
    </h1>
    <div className="info">
      <div>
        <p><strong>Period:</strong> {new Date(filters.dateFrom).toLocaleDateString()} - {new Date(filters.dateTo).toLocaleDateString()}</p>
        <p><strong>Generated:</strong> {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
        <p><strong>Generated By:</strong> {userName}</p>
      </div>
      <div style={{textAlign: 'right'}}>
        <p><strong>Report Type:</strong> {reportType === 'daily' ? 'Daily Summary' : 'Detailed Analysis'}</p>
        <p><strong>Category Filter:</strong> {getCategoryFilterText()}</p>
        <p><strong>Fuel Type Filter:</strong> {getFuelTypeFilterText()}</p>
        <p><strong>Document #:</strong> FR-{new Date().getFullYear()}-{String(new Date().getMonth() + 1).padStart(2, '0')}-{String(new Date().getDate()).padStart(2, '0')}</p>
      </div>
    </div>
  </div>

  {/* Summary Section with Fuel Type Analysis - SIDE BY SIDE */}
  <div className="side-by-side">
    <div className="section">
      <h3>Fuel Type Analysis</h3>
      <div className="breakdown-grid" style={{gridTemplateColumns: 'repeat(2, 1fr)'}}>
        {Object.entries(fuelTypeStats).map(([type, stats]) => (
          <div key={type} className="breakdown-item">
            <h4>{type}</h4>
            <div className="detail">
              <span className="label">Fill-ups:</span>
              <span className="value">{stats.count}</span>
            </div>
            <div className="detail">
              <span className="label">Volume:</span>
              <span className="value" style={{color: '#9333ea'}}>{stats.totalVolume.toFixed(2)} L</span>
            </div>
            <div className="detail">
              <span className="label">Avg Price/L:</span>
              <span className="value">
                {stats.totalVolume > 0 && stats.totalCost > 0 ? (stats.totalCost / stats.totalVolume).toFixed(3) + ' USD' : 'N/A'}
              </span>
            </div>
            <div className="detail">
              <span className="label">Cost:</span>
              <span className="value" style={{color: '#16a34a'}}>{stats.totalCost.toFixed(2)} USD</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="section">
      <h3>Overall Totals</h3>
      <div className="breakdown-grid" style={{gridTemplateColumns: 'repeat(2, 1fr)'}}>
        <div className="breakdown-item">
          <h4>Total Volume</h4>
          <div className="detail" style={{justifyContent: 'center', marginTop: '6px'}}>
            <span className="value" style={{color: '#9333ea', fontSize: '16px', fontWeight: 'bold'}}>{totalVolume.toFixed(2)} L</span>
          </div>
        </div>
        <div className="breakdown-item">
          <h4>Total Cost</h4>
          <div className="detail" style={{justifyContent: 'center', marginTop: '6px'}}>
            <span className="value" style={{color: '#16a34a', fontSize: '16px', fontWeight: 'bold'}}>{totalCost.toFixed(2)} USD</span>
          </div>
          {pendingCount > 0 && (
            <p style={{fontSize: '16px', color: '#ea580c', marginTop: '3px', textAlign: 'center'}}>{pendingCount} pending</p>
          )}
        </div>
      </div>
    </div>
  </div>

  {/* Rest of the content remains the same... */}

  {reportType === 'detailed' ? (
    <>
      {/* Vehicle Mileage & Fuel Efficiency Analysis */}
      {Object.keys(vehicleStats).length > 0 && (
        <div className="section" style={{marginBottom: '5px'}}>
          <h3 className="flex items-center gap-2">
            <Gauge size={20} />
            Vehicle Mileage & Fuel Efficiency
          </h3>
          <div className="breakdown-grid">
            {Object.entries(vehicleStats).map(([vehicle, stats]) => (
              <div key={vehicle} className="breakdown-item">
                <h4>{vehicle}</h4>
                <div className="subtitle">
                  {stats.vehicleType} - {stats.category || 'N/A'}
                  <br />
                  <span style={{color: '#2563eb', fontWeight: 'bold'}}>⛽ {stats.fuelType}</span>
                  <br />
                  {stats.driversArray && stats.driversArray.length > 0 ? (
                    stats.driversArray.length === 1 ? (
                      <span style={{color: '#059669', fontWeight: 'bold'}}>👤 {stats.driversArray[0]}</span>
                    ) : (
                      <span style={{color: '#d97706', fontWeight: 'bold'}}>👥 Multiple Drivers ({stats.driversArray.length})</span>
                    )
                  ) : (
                    stats.primaryDriver && stats.primaryDriver !== 'N/A' ? (
                      <span style={{color: '#059669', fontWeight: 'bold'}}>👤 {stats.primaryDriver}</span>
                    ) : null
                  )}
                </div>
                <div className="detail">
                  <span className="label">Fill-ups:</span>
                  <span className="value">{stats.trips}</span>
                </div>
                {stats.totalDistance !== undefined && (
                  <>
                    <div className="detail">
                      <span className="label">Start Odometer:</span>
                      <span className="value">{stats.firstOdometer?.toFixed(1)} km</span>
                    </div>
                    <div className="detail">
                      <span className="label">End Odometer:</span>
                      <span className="value">{stats.lastOdometer?.toFixed(1)} km</span>
                    </div>
                    <div className="detail">
                      <span className="label">Distance:</span>
                      <span className="value" style={{color: '#ea580c'}}>{stats.totalDistance.toFixed(1)} km</span>
                    </div>
                  </>
                )}
                <div className="detail">
                  <span className="label">Fuel Used:</span>
                  <span className="value" style={{color: '#9333ea'}}>{stats.totalVolume.toFixed(2)} L</span>
                </div>
                {stats.avgFuelEfficiency > 0 && (
                  <div className="detail">
                    <span className="label">Efficiency:</span>
                    <span className="value" style={{color: '#16a34a', fontWeight: 'bold'}}>
                      {stats.avgFuelEfficiency.toFixed(2)} km/L
                    </span>
                  </div>
                )}
                <div className="detail">
                  <span className="label">Total Cost:</span>
                  <span className="value" style={{color: '#16a34a'}}>{stats.totalCost.toFixed(2)} USD</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {Object.keys(categoryStats).length > 0 && (
        <div className="section" style={{marginBottom: '5px'}}>
          <h3>Category Breakdown</h3>
          <div className="breakdown-grid">
            {Object.entries(categoryStats).map(([category, stats]) => (
              <div key={category} className="breakdown-item">
                <h4>{category}</h4>
                <div className="subtitle">{stats.vehicles.size} vehicle(s)</div>
                <div className="detail">
                  <span className="label">Fill-ups:</span>
                  <span className="value">{stats.count}</span>
                </div>
                <div className="detail">
                  <span className="label">Volume:</span>
                  <span className="value" style={{color: '#9333ea'}}>{stats.totalVolume.toFixed(2)} L</span>
                </div>
                <div className="detail">
                  <span className="label">Cost:</span>
                  <span className="value" style={{color: '#16a34a'}}>{stats.totalCost.toFixed(2)} USD</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gasoline Table */}
      {gasolineLogs.length > 0 && (
        <div className="section" style={{marginBottom: '5px'}}>
          <h3>Gasoline Fuel Logs (95 & 98)</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Vehicle</th>
                <th>Type</th>
                <th>Category</th>
                <th>Fuel Type</th>
                <th>Driver</th>
                <th>Odometer</th>
                <th>Volume</th>
                <th>Price/L</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {gasolineLogs.map(log => {
                const pricePerLiter = log.Cost && log.Volume > 0 ? (log.Cost / log.Volume) : null;

                return (
                  <tr key={log.FuelLogID}>
                    <td style={{fontSize: '16px'}}>
                      {new Date(log.FuelDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </td>
                    <td style={{fontWeight: '600', fontSize: '16px'}}>{log.VehicleNumber}</td>
                    <td style={{fontSize: '12px', color: '#6b7280'}}>{log.VehicleType}</td>
                    <td style={{fontSize: '12px'}}>{log.Category || 'N/A'}</td>
                    <td style={{fontWeight: '600', color: '#2563eb', fontSize: '12px'}}>{log.FuelType}</td>
                    <td style={{fontSize: '12px'}}>{log.DriverName || '-'}</td>
                    <td style={{fontSize: '12px'}}>{log.OdometerReading.toFixed(1)} km</td>
                    <td style={{fontWeight: '600', fontSize: '16px'}}>{log.Volume.toFixed(2)} L</td>
                    <td style={{fontSize: '12px'}}>
                      {pricePerLiter ? (
                        <span>{pricePerLiter.toFixed(3)} USD</span>
                      ) : (
                        <span className="pending">Pending</span>
                      )}
                    </td>
                    <td style={{fontSize: '16px'}}>
                      {log.Cost ? (
                        <span style={{fontWeight: 'bold'}}>{log.Cost.toFixed(2)} USD</span>
                      ) : (
                        <span className="pending">Pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="7" style={{textAlign: 'right', fontWeight: 'bold', fontSize: '16px'}}>GASOLINE TOTALS:</td>
                <td style={{fontWeight: 'bold', fontSize: '16px'}}>{gasolineTotalVolume.toFixed(2)} L</td>
                <td style={{fontWeight: 'bold', fontSize: '16px'}}>{gasolineAvgCostPerLiter > 0 ? gasolineAvgCostPerLiter.toFixed(3) : '0.000'} USD</td>
                <td style={{fontWeight: 'bold', color: '#16a34a', fontSize: '16px'}}>{gasolineTotalCost.toFixed(2)} USD</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Mazout/Diesel Table */}
      {mazoutLogs.length > 0 && (
        <div className="section" style={{marginBottom: '5px'}}>
          <h3>Mazout & Diesel Fuel Logs</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Vehicle</th>
                <th>Type</th>
                <th>Category</th>
                <th>Fuel Type</th>
                <th>Driver</th>
                <th>Odometer</th>
                <th>Volume</th>
                <th>Price/L</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {mazoutLogs.map(log => {
                const pricePerLiter = log.Cost && log.Volume > 0 ? (log.Cost / log.Volume) : null;

                return (
                  <tr key={log.FuelLogID}>
                    <td style={{fontSize: '16px'}}>
                      {new Date(log.FuelDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </td>
                    <td style={{fontWeight: '600', fontSize: '16px'}}>{log.VehicleNumber}</td>
                    <td style={{fontSize: '12px', color: '#6b7280'}}>{log.VehicleType}</td>
                    <td style={{fontSize: '12px'}}>{log.Category || 'N/A'}</td>
                    <td style={{fontWeight: '600', color: '#ea580c', fontSize: '12px'}}>{log.FuelType}</td>
                    <td style={{fontSize: '12px'}}>{log.DriverName || '-'}</td>
                    <td style={{fontSize: '12px'}}>{log.OdometerReading.toFixed(1)} km</td>
                    <td style={{fontWeight: '600', fontSize: '16px'}}>{log.Volume.toFixed(2)} L</td>
                    <td style={{fontSize: '12px'}}>
                      {pricePerLiter ? (
                        <span>{pricePerLiter.toFixed(3)} USD</span>
                      ) : (
                        <span className="pending">Pending</span>
                      )}
                    </td>
                    <td style={{fontSize: '16px'}}>
                      {log.Cost ? (
                        <span style={{fontWeight: 'bold'}}>{log.Cost.toFixed(2)} USD</span>
                      ) : (
                        <span className="pending">Pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="7" style={{textAlign: 'right', fontWeight: 'bold', fontSize: '16px'}}>MAZOUT/DIESEL TOTALS:</td>
                <td style={{fontWeight: 'bold', fontSize: '16px'}}>{mazoutTotalVolume.toFixed(2)} L</td>
                <td style={{fontWeight: 'bold', fontSize: '16px'}}>{mazoutAvgCostPerLiter > 0 ? mazoutAvgCostPerLiter.toFixed(3) : '0.000'} USD</td>
                <td style={{fontWeight: 'bold', color: '#16a34a', fontSize: '16px'}}>{mazoutTotalCost.toFixed(2)} USD</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  ) : (
    <>
      {/* Daily View - Gasoline Table */}
      {gasolineLogs.length > 0 && (
        <div className="section" style={{marginBottom: '12px'}}>
          <h3>Gasoline Fuel Logs (95 & 98)</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Vehicle</th>
                <th>Type</th>
                <th>Fuel</th>
                <th>Driver</th>
                <th>Volume</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {gasolineLogs.map(log => (
                <tr key={log.FuelLogID}>
                  <td style={{fontSize: '16px'}}>
                    {new Date(log.FuelDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td style={{fontWeight: '600', fontSize: '16px'}}>{log.VehicleNumber}</td>
                  <td style={{fontSize: '12px', color: '#6b7280'}}>{log.VehicleType}</td>
                  <td style={{fontSize: '12px', color: '#2563eb', fontWeight: '600'}}>{log.FuelType}</td>
                  <td style={{fontSize: '12px'}}>{log.DriverName || '-'}</td>
                  <td style={{fontWeight: '600', fontSize: '16px'}}>{log.Volume.toFixed(1)} L</td>
                  <td style={{fontSize: '16px'}}>
                    {log.Cost ? (
                      <span style={{fontWeight: 'bold'}}>{log.Cost.toFixed(2)} USD</span>
                    ) : (
                      <span className="pending">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5" style={{textAlign: 'right', fontSize: '16px'}}>GASOLINE TOTAL:</td>
                <td style={{fontSize: '16px', fontWeight: 'bold'}}>{gasolineTotalVolume.toFixed(1)} L</td>
                <td style={{fontWeight: 'bold', fontSize: '16px'}}>{gasolineTotalCost.toFixed(2)} USD</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Daily View - Mazout/Diesel Table */}
      {mazoutLogs.length > 0 && (
        <div className="section" style={{marginBottom: '12px'}}>
          <h3>Mazout & Diesel Fuel Logs</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Vehicle</th>
                <th>Type</th>
                <th>Fuel</th>
                <th>Driver</th>
                <th>Volume</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {mazoutLogs.map(log => (
                <tr key={log.FuelLogID}>
                  <td style={{fontSize: '16px'}}>
                    {new Date(log.FuelDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td style={{fontWeight: '600', fontSize: '16px'}}>{log.VehicleNumber}</td>
                  <td style={{fontSize: '12px', color: '#6b7280'}}>{log.VehicleType}</td>
                  <td style={{fontSize: '12px', color: '#ea580c', fontWeight: '600'}}>{log.FuelType}</td>
                  <td style={{fontSize: '12px'}}>{log.DriverName || '-'}</td>
                  <td style={{fontWeight: '600', fontSize: '16px'}}>{log.Volume.toFixed(1)} L</td>
                  <td style={{fontSize: '16px'}}>
                    {log.Cost ? (
                      <span style={{fontWeight: 'bold'}}>{log.Cost.toFixed(2)} USD</span>
                    ) : (
                      <span className="pending">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5" style={{textAlign: 'right', fontSize: '16px'}}>MAZOUT/DIESEL TOTAL:</td>
                <td style={{fontSize: '16px', fontWeight: 'bold'}}>{mazoutTotalVolume.toFixed(1)} L</td>
                <td style={{fontWeight: 'bold', fontSize: '16px'}}>{mazoutTotalCost.toFixed(2)} USD</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  )}

  {/* Signature Section */}
  <div className="signature-section">
    <div className="signature-box">
      <div className="signature-line">
        Prepared By: {userName}
      </div>
      <div className="signature-label">Signature & Date</div>
    </div>
    <div className="signature-box">
      <div className="signature-line">
        Revised By: Charles Naim
      </div>
      <div className="signature-label">Signature & Date</div>
    </div>
    <div className="signature-box">
      <div className="signature-line">
        Approved By: P. George Bou Chaaya
      </div>
      <div className="signature-label">Stamp & Signature</div>
    </div>
  </div>

  {/* Footer */}
  <div className="report-footer">
    <p><strong>Collège des Apôtres - Jounieh</strong> - Petrol Station Management System</p>
    <p style={{marginTop: '5px'}}>Report generated on: {new Date().toLocaleString()}</p>
  </div>
</div>
            )}
          </div>
        </div>
      </div>

      {/* Inline CSS for screen display */}
<style jsx>{`
  .report-header {
    border-bottom: 3px solid #1f2937;
    padding-bottom: 15px;
    margin-bottom: 20px;
  }

  .report-header h1 {
    font-size: 28px;
    color: #1f2937;
    margin-bottom: 8px;
    font-weight: bold;
  }

  .report-header .info {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: #4b5563;
  }

  .report-header .info strong {
    color: #1f2937;
  }

  .side-by-side {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 10px;
  }

  .section {
    border: 2px solid #d1d5db;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 10px;
    background: white;
  }

  .section h3 {
    font-size: 18px;
    color: #1f2937;
    margin-bottom: 10px;
    font-weight: bold;
  }

  .breakdown-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  .breakdown-item {
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px;
    background: #f9fafb;
  }

  .breakdown-item h4 {
    font-size: 15px;
    color: #1f2937;
    margin-bottom: 4px;
    font-weight: bold;
  }

  .breakdown-item .subtitle {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 8px;
    line-height: 1.4;
  }

  .breakdown-item .detail {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    margin-bottom: 3px;
  }

  .breakdown-item .detail .label {
    color: #6b7280;
  }

  .breakdown-item .detail .value {
    font-weight: 600;
  }

  .section table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: fixed;
  }

  .section table th:nth-child(1) { width: 8%; } /* Date */
  .section table th:nth-child(2) { width: 9%; } /* Vehicle */
  .section table th:nth-child(3) { width: 11%; } /* Type */
  .section table th:nth-child(4) { width: 9%; } /* Category */
  .section table th:nth-child(5) { width: 10%; } /* Fuel Type */
  .section table th:nth-child(6) { width: 14%; } /* Driver */
  .section table th:nth-child(7) { width: 10%; } /* Odometer */
  .section table th:nth-child(8) { width: 10%; } /* Volume */
  .section table th:nth-child(9) { width: 9%; } /* Price/L */
  .section table th:nth-child(10) { width: 10%; } /* Cost */

  .section table td {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .section thead tr {
    background: #f3f4f6;
    border: 2px solid #d1d5db;
  }

  .section th {
    padding: 10px 8px;
    text-align: left;
    font-weight: bold;
    color: #374151;
    border: 1px solid #d1d5db;
  }

  .section tbody tr {
    border: 1px solid #d1d5db;
  }

  .section tbody tr:hover {
    background: #f9fafb;
  }

  .section td {
    padding: 9px;
    border: 1px solid #d1d5db;
  }

  .section tfoot tr {
    background: #e5e7eb;
    border: 2px solid #d1d5db;
    font-weight: bold;
  }

  .section tfoot td {
    padding: 10px 8px;
  }

  .pending {
    font-style: italic;
    color: #ea580c;
  }

  .signature-section {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 30px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 2px solid #e5e7eb;
  }

  .signature-box {
    text-align: center;
  }

  .signature-line {
    border-top: 2px solid #1f2937;
    margin-top: 60px;
    padding-top: 8px;
    font-weight: bold;
    color: #1f2937;
    font-size: 15px;
  }

  .signature-label {
    font-size: 13px;
    color: #6b7280;
    margin-top: 4px;
  }

  .report-footer {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 2px solid #d1d5db;
    text-align: center;
    font-size: 12px;
    color: #6b7280;
  }

  @media screen and (max-width: 768px) {
    .breakdown-grid,
    .side-by-side,
    .signature-section {
      grid-template-columns: 1fr;
    }
  }
`}</style>
    </>
  );
};

export default FuelReportModal;